import * as React from "react";
import { createPortal } from "../../windowManager/browserFenster/fensterPortal";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { REFINED } from "../../risikoanalyseStep/tokens";
import { PhoneInput } from "./PhoneInput";
import { EmailInput } from "./EmailInput";
import { normalizeEmail, normalizePhone } from "../validation";
import { wegPruefen } from "../kommunikation/weitereWege";
import { rufnummernArt } from "@kmvs/forms-core";
import { vorhandenerWeg, type KontaktWeg } from "../kommunikation/doppelteWege";
import { useNummernAndererKontakte } from "../kommunikation/useNummernAndererKontakte";
import {
  KommHinweise,
  andererKontaktKommHinweis,
  doppeltKommHinweis,
  type KommHinweis,
} from "../kommunikation/TelefonMobilHinweis";
import { UnternehmenMehrfachWahl, gesperrteFirma } from "./UnternehmenMehrfachWahl";
import { elementMitId } from "../../windowManager/browserFenster/fensterRegister";

export interface WeitereKommunikationEintrag {
  id: string;
  type: "phone" | "mobile" | "email" | "fax" | "website" | "other";
  label: string;
  value: string;
  zuordnung?: "privat" | "geschaeftlich";
  erreichbarkeit?: string;
  /**
   * Unternehmen eines geschäftlichen Wegs (seit 02.10.2026 mehrere). Fehlt das
   * Feld, gilt die Regel aus utils/geschaeftlichFirma.ts (genau eine aktive
   * Firma ist immer gesetzt); `[]` = bewusst keins.
   */
  firmen?: Array<{ id?: number; name: string }>;
  /** Altstand (eine Firma) — wird noch gelesen, nicht mehr geschrieben. */
  companyId?: number;
  /** Altstand (eine Firma). */
  companyName?: string;
  /** Altstand: „(Keine Firma)“ bewusst gewählt. */
  keineFirma?: boolean;
}

const useStyles = makeStyles({
  scrim: {
    position: "fixed",
    inset: 0,
    zIndex: 100002,
    backgroundColor: "rgba(20,8,20,0.38)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "24px",
    fontFamily: REFINED.fontUI,
  },
  dialog: {
    width: "min(520px, 100%)",
    backgroundColor: REFINED.white,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("14px"),
    boxShadow: REFINED.cardShadow,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  head: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: `2px solid ${REFINED.goldLine}`,
  },
  title: {
    margin: 0,
    fontFamily: REFINED.fontUI,
    fontSize: "1.1rem",
    fontWeight: 700,
    color: REFINED.ink,
  },
  closeBtn: {
    background: "none",
    ...shorthands.borderWidth("0"),
    fontSize: "1.2rem",
    cursor: "pointer",
    color: REFINED.ink3,
    ":hover": { color: REFINED.ink },
  },
  body: {
    padding: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
    fontFamily: REFINED.fontUI,
  },
  groupLabel: {
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.875rem",
    color: REFINED.ink,
    marginBottom: "8px",
  },
  radioGroup: {
    display: "flex",
    flexWrap: "wrap",
    gap: "18px",
    alignItems: "center",
  },
  radioLabel: {
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    cursor: "pointer",
    fontSize: "0.9rem",
    color: REFINED.ink,
    "& input": {
      accentColor: REFINED.aub,
      cursor: "pointer",
      width: "16px",
      height: "16px",
    },
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 14px",
    fontFamily: REFINED.fontUI,
    fontSize: "0.9rem",
    color: REFINED.ink,
    backgroundColor: REFINED.fieldBg,
    ...shorthands.border("1px", "solid", REFINED.line),
    ...shorthands.borderRadius("10px"),
    outline: "none",
    ":focus": {
      ...shorthands.borderColor(REFINED.aub),
      backgroundColor: REFINED.white,
    },
  },
  foot: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "12px",
    padding: "14px 20px",
    backgroundColor: REFINED.fieldBg,
    borderTop: `1px solid ${REFINED.line}`,
  },
  cancelBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: REFINED.white,
    color: REFINED.aub,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 18px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub50 },
  },
  submitBtn: {
    ...shorthands.border("1.5px", "solid", REFINED.aub),
    ...shorthands.borderRadius("8px"),
    backgroundColor: "#b388aa",
    color: REFINED.white,
    fontFamily: REFINED.fontUI,
    fontWeight: 600,
    fontSize: "0.88rem",
    padding: "8px 20px",
    cursor: "pointer",
    ":hover": { backgroundColor: REFINED.aub },
    ":disabled": { opacity: 0.5, cursor: "not-allowed" },
  },
});

export const WeitereKommunikationDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onAdd?: (eintrag: WeitereKommunikationEintrag) => void;
  onSave?: (eintrag: WeitereKommunikationEintrag) => void;
  showZuordnung?: boolean;
  defaultZuordnung?: "privat" | "geschaeftlich";
  /**
   * Nur ohne Zuordnungs-Auswahl: die Nummer gehört zu einer Firma (Firmenanlage),
   * eine Durchwahl „-" bleibt also erhalten. Ohne Angabe entscheidet
   * `defaultZuordnung`.
   */
  geschaeftlich?: boolean;
  /** Wählbare Unternehmen — nur aktive (ausgeschiedene stehen nicht zur Auswahl). */
  availableCompanies?: Array<{ id?: number; name: string }>;
  checkExisting?: (
    type: "phone" | "mobile" | "fax" | "email",
    zuordnung: "privat" | "geschaeftlich",
    firmen: Array<{ id?: number; name: string }>
  ) => boolean;
  /**
   * Alle Kommunikationsfelder/-wege, die der Kontakt schon hat: Steht der
   * eingegebene Wert dort schon, erscheint ein Hinweis („Diese Nummer steht
   * schon unter Telefon (privat)."). „Hinzufügen" bleibt trotzdem möglich.
   */
  vorhandeneWege?: readonly KontaktWeg[];
  /** Der Kontakt selbst (Akte) — gilt beim Abgleich mit anderen Kontakten nicht als „anderer". */
  eigeneKontaktId?: number | null;
}> = ({
  open,
  onClose,
  onAdd,
  onSave,
  showZuordnung = true,
  defaultZuordnung = "privat",
  geschaeftlich,
  availableCompanies,
  checkExisting,
  vorhandeneWege,
  eigeneKontaktId,
}) => {
  const s = useStyles();
  const [type, setType] = React.useState<"phone" | "mobile" | "fax" | "email">("phone");
  const [zuordnung, setZuordnung] = React.useState<"privat" | "geschaeftlich">(defaultZuordnung);
  const [firmen, setFirmen] = React.useState<Array<{ id?: number; name: string }>>([]);
  const [label, setLabel] = React.useState("");
  const [value, setValue] = React.useState("");

  React.useEffect(() => {
    if (open) {
      setType("phone");
      setZuordnung(defaultZuordnung);
      // Genau ein Unternehmen → immer gesetzt; ab zwei darf es leer bleiben.
      const einziges = gesperrteFirma(availableCompanies ?? []);
      setFirmen(einziges ? [einziges] : []);
      setLabel("");
      setValue("");
    }
  }, [open, defaultZuordnung, availableCompanies]);

  // Fokus ins Wertfeld beim Öffnen und nach dem Art-Wechsel (vorher autoFocus am rohen Feld).
  React.useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => elementMitId("weitere-komm-wert")?.focus(), 0);
    return () => window.clearTimeout(t);
  }, [open, type]);

  const showBezeichnung = React.useMemo(() => {
    if (checkExisting) {
      return checkExisting(type, zuordnung, firmen);
    }
    if (zuordnung === "privat") return true;
    if (!availableCompanies || availableCompanies.length === 0) return true;
    return firmen.length > 0;
  }, [checkExisting, type, zuordnung, firmen, availableCompanies]);

  // Telefonformat wie in allen anderen Feldern (T-59): „+49 221 123456-78",
  // die Durchwahl nur bei geschäftlichen Nummern.
  const istGeschaeftlich = showZuordnung
    ? zuordnung === "geschaeftlich"
    : geschaeftlich ?? defaultZuordnung === "geschaeftlich";
  const wertFehler = value.trim() ? wegPruefen(type, value) : null;

  // Unternehmen sind keine Pflicht (User 02.10.2026): ab zwei darf die Auswahl leer bleiben.
  const canSubmit = Boolean(value.trim() && !wertFehler);

  // Art-Wechsel zwischen E-Mail und Nummer: der alte Wert passt nicht ins andere Feld.
  const waehleTyp = (neu: "phone" | "mobile" | "fax" | "email") => {
    if ((neu === "email") !== (type === "email")) setValue("");
    setType(neu);
  };

  // Hinweise unter dem Wert (User 01.10.2026) — nur Hinweise, nie blockierend:
  // (a) Wert steht schon in einem anderen Feld/Weg dieses Kontakts,
  // (b) Art passt nicht zur Nummer: Mobilnummer bei „Telefon" bzw. Festnetz
  //     bei „Mobil" (gleiche Erkennung wie unter den Feldern, #1974; unklare,
  //     ausländische und Sondernummern → keine Aussage; Fax wird nicht geprüft),
  // (c) Nummer gehört schon zu einem anderen Kontakt.
  const artHinweis: KommHinweis | null = (() => {
    if (type !== "phone" && type !== "mobile") return null;
    const art = rufnummernArt(value);
    if (type === "phone" && art === "mobil")
      return { id: "art", text: "Das ist eine Mobilnummer.", knopf: "Als Mobil eintragen", onKnopf: () => waehleTyp("mobile") };
    if (type === "mobile" && art === "festnetz")
      return { id: "art", text: "Das ist eine Festnetznummer.", knopf: "Als Telefon eintragen", onKnopf: () => waehleTyp("phone") };
    return null;
  })();
  const doppeltHinweis = open ? doppeltKommHinweis(type, vorhandenerWeg(type, value, vorhandeneWege)) : null;
  const andere = useNummernAndererKontakte(
    open && !doppeltHinweis ? [{ key: "popup", typ: type, wert: value, feldname: "" }] : [],
    { eigeneId: eigeneKontaktId },
  );
  const wertHinweise = <KommHinweise eigen={[artHinweis, doppeltHinweis, andererKontaktKommHinweis(andere.get("popup"))]} />;

  if (!open) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    const item: WeitereKommunikationEintrag = {
      id: "comm-" + Date.now() + "-" + Math.random().toString(36).substring(2, 6),
      type,
      label: label.trim(),
      value:
        type === "email"
          ? normalizeEmail(value)
          : normalizePhone(value, { geschaeftlich: istGeschaeftlich }),
      ...(showZuordnung ? { zuordnung } : {}),
      ...(showZuordnung && zuordnung === "geschaeftlich" && availableCompanies?.length ? { firmen } : {}),
    };
    if (onSave) onSave(item);
    else if (onAdd) onAdd(item);
    onClose();
  };

  const isEmail = type === "email";

  return createPortal(
    <div className={s.scrim} onClick={onClose}>
      <div className={s.dialog} onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className={s.head}>
          <h2 className={s.title}>Weiterer Kommunikationsweg</h2>
          <button type="button" className={s.closeBtn} onClick={onClose} aria-label="Schließen">
            ✕
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className={s.body}>
            {/* Art */}
            <div>
              <div className={s.groupLabel}>Art</div>
              <div className={s.radioGroup}>
                <label className={s.radioLabel}>
                  <input
                    type="radio"
                    name="commType"
                    checked={type === "phone"}
                    onChange={() => waehleTyp("phone")}
                  />
                  <span>Telefon</span>
                </label>
                <label className={s.radioLabel}>
                  <input
                    type="radio"
                    name="commType"
                    checked={type === "mobile"}
                    onChange={() => waehleTyp("mobile")}
                  />
                  <span>Mobil</span>
                </label>
                <label className={s.radioLabel}>
                  <input
                    type="radio"
                    name="commType"
                    checked={type === "fax"}
                    onChange={() => waehleTyp("fax")}
                  />
                  <span>Fax</span>
                </label>
                <label className={s.radioLabel}>
                  <input
                    type="radio"
                    name="commType"
                    checked={type === "email"}
                    onChange={() => waehleTyp("email")}
                  />
                  <span>E-Mail</span>
                </label>
              </div>
            </div>

            {/* Zuordnung (Privat / Geschäftlich) */}
            {showZuordnung && (
              <div>
                <div className={s.groupLabel}>Zuordnung</div>
                <div className={s.radioGroup}>
                  <label className={s.radioLabel}>
                    <input
                      type="radio"
                      name="commZuordnung"
                      checked={zuordnung === "privat"}
                      onChange={() => setZuordnung("privat")}
                    />
                    <span>Privat</span>
                  </label>
                  <label className={s.radioLabel}>
                    <input
                      type="radio"
                      name="commZuordnung"
                      checked={zuordnung === "geschaeftlich"}
                      onChange={() => setZuordnung("geschaeftlich")}
                    />
                    <span>Geschäftlich</span>
                  </label>
                </div>
              </div>
            )}

            {/* Unternehmen (bei geschäftlicher Zuordnung und vorhandenen Firmen) —
                Mehrfachauswahl, keine Pflicht (User 02.10.2026). */}
            {showZuordnung && zuordnung === "geschaeftlich" && availableCompanies && availableCompanies.length > 0 && (
              <div>
                <label className={s.groupLabel} htmlFor="weitere-komm-unternehmen" style={{ display: "block" }}>
                  Unternehmen
                </label>
                <UnternehmenMehrfachWahl
                  id="weitere-komm-unternehmen"
                  firmen={availableCompanies}
                  gewaehlt={firmen}
                  onChange={setFirmen}
                />
              </div>
            )}

            {/* Nummer / Wert */}
            {isEmail ? (
              <EmailInput
                label="E-Mail-Adresse"
                name="weitere-komm-wert"
                value={value}
                onChange={setValue}
                actionButton={false}
                unterFeld={wertHinweise}
              />
            ) : (
              <PhoneInput
                label="Nummer"
                name="weitere-komm-wert"
                kind={type === "fax" ? "fax" : type === "phone" ? "landline" : "mobile"}
                value={value}
                onChange={setValue}
                actionButton={false}
                geschaeftlich={istGeschaeftlich}
                unterFeld={wertHinweise}
              />
            )}

            {/* Bezeichnung (nur wenn schon eine Nummer/Adresse für Firma/Kontext vorhanden ist, sonst erst dann) */}
            {showBezeichnung && (
              <div>
                <div className={s.groupLabel}>Bezeichnung (optional)</div>
                <input
                  type="text"
                  value={label}
                  onChange={(e) => setLabel(e.target.value)}
                  placeholder={
                    isEmail
                      ? "z. B. Support, Rechnungen"
                      : zuordnung === "privat"
                      ? "z. B. Ferienhaus, Zweitwohnsitz"
                      : "z. B. Zentrale, Durchwahl"
                  }
                  className={s.input}
                />
              </div>
            )}
          </div>

          <div className={s.foot}>
            <button type="button" className={s.cancelBtn} onClick={onClose}>
              Abbrechen
            </button>
            <button
              type="submit"
              className={s.submitBtn}
              disabled={!canSubmit}
              style={canSubmit ? { backgroundColor: REFINED.aub } : undefined}
            >
              Hinzufügen
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
};

export default WeitereKommunikationDialog;
